<script setup>
import { Button } from '@dashhudson/dashing-ui/button';
import { EditableSelect } from '@dashhudson/dashing-ui/editable-select';
import { computed, onMounted, ref, toRefs, watch } from 'vue';

import { createContentTag } from '@/apis/library';
import { useContentTags } from '@/app/settings/composables/useContentTags';
import { useAuthStore } from '@/stores/auth';
import { useContentTagsStore } from '@/stores/content-tags';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  selectedMedia: { type: Array, required: true },
});

const tags = ref([]);
const { selectedMedia } = toRefs(props);

const selectedMediaWithoutDMedStories = computed(() =>
  selectedMedia.value.filter((media) => !media?.instagramStoryFrame?.importedViaDm),
);

const { availableContentTags, onSaveClick, addContentTagsTooltip } = useContentTags(
  selectedMediaWithoutDMedStories,
);

const authStore = useAuthStore();
const contentTagsStore = useContentTagsStore();

const selectedContentTags = ref([]);

const emit = defineEmits(['onActionComplete']);

async function onAddNewContentTag(contentTagName) {
  if (tags.value.map((contentTag) => contentTag.label).includes(contentTagName)) {
    throw new Error('Content tag already exists');
  }

  const organizationId = authStore.currentBrand?.organizationId;
  const brandIds = [authStore.currentBrand?.id];
  const response = await createContentTag({ organizationId, name: contentTagName, brandIds });

  return {
    label: contentTagName,
    value: response?.data?.id,
  };
}
async function onBulkAddContentTags(contentTagIds) {
  await onSaveClick(contentTagIds);
  emit('onActionComplete');
}

watch(
  () => availableContentTags.value,
  () => {
    tags.value = [...availableContentTags.value];
  },
);

onMounted(() => {
  contentTagsStore.getAllOrganizationsContentTags();
});
</script>

<template>
  <EditableSelect
    v-model="selectedContentTags"
    v-model:options="tags"
    searchable
    search-placeholder="Search Tags"
    empty-text="No content tags found"
    :dropdown="{ title: 'Content Tags', placement: 'bottom-end' }"
    :loading="contentTagsStore.pending.allOrganizationsContentTags"
    :add-new-callback="onAddNewContentTag"
    @update:model-value="onBulkAddContentTags"
  >
    <Button
      v-dash-tooltip="addContentTagsTooltip"
      size="xs"
      severity="bulk"
      icon="tag"
      :disabled="props.disabled"
    />
  </EditableSelect>
</template>
